import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Copy,
  FilePlus2,
  KeyRound,
  Link2,
  LogOut,
  Palette,
  Pause,
  Play,
  RadioTower,
  RefreshCcw,
  ShieldCheck,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import type {
  AdminSession,
  FileRecord,
  ShareRecord,
  ShareTheme,
} from "@xiaotian-vault/shared";
import { DEFAULT_SHARE_THEME, SHARE_THEMES } from "@xiaotian-vault/shared";
import { BrandMark } from "../components/BrandMark.js";
import { LocaleToggle } from "../components/LocaleToggle.js";
import { ApiClientError, createApiClient } from "../lib/api.js";
import { copyText } from "../lib/clipboard.js";
import {
  DEMO_PASSKEY,
  DEMO_SHARE_TOKEN,
  demoAdminSession,
  demoFiles,
  demoPublicShareTheme,
  demoUploadedFileRecord,
  isDemoAdminRoute,
  saveDemoPublicShareTheme,
} from "../lib/demo.js";
import {
  formatBytes,
  formatDate,
  formatDuration,
  shareUrl,
  withLocaleParam,
} from "../lib/format.js";
import { useI18n } from "../lib/i18n.js";
import {
  createParallelUpload,
  type UploadController,
} from "../lib/parallel-upload.js";
import { uploadChunkSize, uploadParallelism } from "../lib/upload.js";

const api = createApiClient();

type UploadState = {
  name: string;
  progress: number;
  bytesUploaded: number;
  bytesTotal: number;
  startedAt: number | null;
  status: "idle" | "uploading" | "paused" | "done" | "error";
  error?: string;
};

type ShareReveal = {
  passkey: string;
  publicUrl: string;
};

export function AdminPage() {
  const { locale, t } = useI18n();
  const demoMode = isDemoAdminRoute();
  const [session, setSession] = useState<AdminSession | null>(null);
  const [files, setFiles] = useState<FileRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [publicTheme, setPublicTheme] =
    useState<ShareTheme>(DEFAULT_SHARE_THEME);
  const [savedPublicTheme, setSavedPublicTheme] =
    useState<ShareTheme>(DEFAULT_SHARE_THEME);
  const [themeSaving, setThemeSaving] = useState<ShareTheme | null>(null);
  const [uploadState, setUploadState] = useState<UploadState>({
    name: "",
    progress: 0,
    bytesUploaded: 0,
    bytesTotal: 0,
    startedAt: null,
    status: "idle",
  });
  const [expiryByFile, setExpiryByFile] = useState<Record<string, string>>({});
  const [shareReveal, setShareReveal] = useState<ShareReveal | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const uploadRef = useRef<UploadController | null>(null);

  useEffect(() => {
    if (demoMode) {
      const theme = demoPublicShareTheme();
      setPublicTheme(theme);
      setSavedPublicTheme(theme);
      setSession(demoAdminSession());
      setFiles(demoFiles());
      setLoading(false);
      return;
    }

    let alive = true;
    api
      .me()
      .then(async (nextSession) => {
        if (!alive) return;
        setSession(nextSession);
        const [nextFiles, settings] = await Promise.all([
          api.listFiles(),
          api.getSettings(),
        ]);
        if (!alive) return;
        setFiles(nextFiles);
        applySettings(settings);
      })
      .catch(() => undefined)
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [demoMode]);

  function applySettings(settings: { publicShareTheme: ShareTheme }) {
    setPublicTheme(settings.publicShareTheme);
    setSavedPublicTheme(settings.publicShareTheme);
  }

  async function loadFiles() {
    if (demoMode) {
      setFiles(demoFiles());
      return;
    }
    const result = await api.listFiles();
    setFiles(result);
  }

  async function login(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    const form = new FormData(event.currentTarget);
    try {
      const next = await api.login({
        username: String(form.get("username") || ""),
        password: String(form.get("password") || ""),
        totpCode: String(form.get("totpCode") || ""),
      });
      setSession(next);
      const [nextFiles, settings] = await Promise.all([
        api.listFiles(),
        api.getSettings(),
      ]);
      setFiles(nextFiles);
      applySettings(settings);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Login failed.");
    }
  }

  async function logout() {
    if (demoMode) {
      setSession(null);
      setFiles([]);
      return;
    }
    await api.logout();
    setSession(null);
    setFiles([]);
  }

  async function savePublicTheme(theme: ShareTheme) {
    if (themeSaving || theme === savedPublicTheme) return;
    setPublicTheme(theme);
    setThemeSaving(theme);

    try {
      if (demoMode) {
        saveDemoPublicShareTheme(theme);
        await new Promise((resolve) => window.setTimeout(resolve, 180));
        applySettings({ publicShareTheme: theme });
      } else {
        applySettings(await api.updateSettings({ publicShareTheme: theme }));
      }
      setToast(t("themeSaved"));
    } catch {
      setPublicTheme(savedPublicTheme);
      setToast(t("themeSaveFailed"));
    } finally {
      setThemeSaving(null);
    }
  }

  function startUpload(file: File) {
    if (demoMode) {
      setUploadState({
        name: file.name,
        progress: 0,
        bytesUploaded: 0,
        bytesTotal: file.size,
        startedAt: Date.now(),
        status: "uploading",
      });

      let progress = 0;
      const interval = window.setInterval(() => {
        progress = Math.min(100, progress + 13);
        const bytesUploaded = Math.round((file.size * progress) / 100);
        setUploadState((current) => ({
          ...current,
          progress,
          bytesUploaded,
          bytesTotal: file.size,
          status: progress >= 100 ? "done" : "uploading",
        }));
        if (progress >= 100) {
          window.clearInterval(interval);
          setFiles((current) => [
            demoUploadedFileRecord({
              name: file.name,
              size: file.size,
              type: file.type,
            }),
            ...current,
          ]);
        }
      }, 180);
      return;
    }

    setUploadState({
      name: file.name,
      progress: 0,
      bytesUploaded: 0,
      bytesTotal: file.size,
      startedAt: Date.now(),
      status: "uploading",
    });
    const upload = createParallelUpload(file, {
      csrfToken: api.csrfToken,
      chunkSize: uploadChunkSize(file.size),
      parallelism: uploadParallelism(),
      onError(err) {
        setUploadState((current) => ({
          ...current,
          status: "error",
          error: err.message,
        }));
      },
      onProgress(bytesUploaded, bytesTotal) {
        setUploadState((current) => ({
          ...current,
          bytesUploaded,
          bytesTotal,
          progress:
            bytesTotal > 0 ? Math.round((bytesUploaded / bytesTotal) * 100) : 0,
          status: "uploading",
        }));
      },
      async onSuccess() {
        setUploadState((current) => ({
          ...current,
          progress: 100,
          status: "done",
        }));
        await loadFiles();
      },
    });
    uploadRef.current = upload;
    upload.start();
  }

  async function createShare(file: FileRecord) {
    if (demoMode) {
      const publicUrl = shareUrl(DEMO_SHARE_TOKEN, locale);
      const copied = await copyText(shareDetailsText(publicUrl, DEMO_PASSKEY));
      setShareReveal({ passkey: DEMO_PASSKEY, publicUrl });
      setFiles((current) =>
        current.map((item) => {
          if (item.id !== file.id || item.shares.length > 0) return item;
          const now = new Date().toISOString();
          return {
            ...item,
            shares: [
              {
                id: `demo-share-${item.id}`,
                token: DEMO_SHARE_TOKEN,
                fileId: item.id,
                passkey: DEMO_PASSKEY,
                enabled: true,
                expiresAt: null,
                createdAt: now,
                updatedAt: now,
                downloadCount: 0,
                lastDownloadedAt: null,
              },
            ],
          };
        }),
      );
      setToast(
        copied
          ? `${t("shareCreated")} · ${t("shareDetailsCopied")}`
          : `${t("shareCreated")} · ${t("copyFailed")}`,
      );
      return;
    }

    const expiryValue = expiryByFile[file.id];
    const expiresAt = expiryValue ? new Date(expiryValue).toISOString() : null;
    const result = await api.createShare(file.id, expiresAt);
    await loadFiles();
    const publicUrl = withLocaleParam(result.publicUrl, locale);
    const copied = await copyText(shareDetailsText(publicUrl, result.passkey));
    setShareReveal({ passkey: result.passkey, publicUrl });
    setToast(
      copied
        ? `${t("shareCreated")} · ${t("shareDetailsCopied")}`
        : `${t("shareCreated")} · ${t("copyFailed")}`,
    );
  }

  async function updateShare(share: ShareRecord, enabled: boolean) {
    if (demoMode) {
      setFiles((current) =>
        current.map((file) => ({
          ...file,
          shares: file.shares.map((item) =>
            item.id === share.id
              ? { ...item, enabled, updatedAt: new Date().toISOString() }
              : item,
          ),
        })),
      );
      return;
    }

    await api.updateShare(share.id, { enabled });
    await loadFiles();
  }

  async function regenerate(share: ShareRecord) {
    if (demoMode) {
      const copied = await copyText(DEMO_PASSKEY);
      setShareReveal({
        passkey: DEMO_PASSKEY,
        publicUrl: shareUrl(share.token, locale),
      });
      setToast(copied ? t("passkeyCopied") : t("copyFailed"));
      return;
    }

    const result = await api.regeneratePasskey(share.id);
    await loadFiles();
    const copied = await copyText(result.passkey);
    setShareReveal({
      passkey: result.passkey,
      publicUrl: shareUrl(result.share.token, locale),
    });
    setToast(copied ? t("passkeyCopied") : t("copyFailed"));
  }

  async function copyShareDetails(publicUrl: string, passkey: string | null) {
    if (!passkey) {
      setToast(t("passkeyUnavailable"));
      return;
    }

    setToast(
      (await copyText(shareDetailsText(publicUrl, passkey)))
        ? t("shareDetailsCopied")
        : t("copyFailed"),
    );
  }

  async function deleteFile(file: FileRecord) {
    if (demoMode) {
      setConfirmDeleteId(null);
      setFiles((current) => current.filter((item) => item.id !== file.id));
      return;
    }

    await api.deleteFile(file.id);
    setConfirmDeleteId(null);
    await loadFiles();
  }

  const totalBytes = useMemo(
    () => files.reduce((sum, file) => sum + file.sizeBytes, 0),
    [files],
  );
  const uploadMetrics = uploadDetails(uploadState);
  const shareRevealUrl = shareReveal
    ? withLocaleParam(shareReveal.publicUrl, locale)
    : "";

  if (loading) {
    return <main className="admin-shell loading">{t("loading")}</main>;
  }

  if (!session) {
    return (
      <main className="admin-shell login-screen">
        <header className="topbar">
          <BrandMark />
          <LocaleToggle />
        </header>
        <form className="login-panel" onSubmit={login}>
          <span className="login-badge">
            <KeyRound size={16} />
            2FA
          </span>
          <h1>{t("admin")}</h1>
          <p>{t("adminSubtitle")}</p>
          {error && <div className="status-line error">{error}</div>}
          <label>
            <span>{t("username")}</span>
            <input name="username" autoComplete="username" required />
          </label>
          <label>
            <span>{t("password")}</span>
            <input
              name="password"
              type="password"
              autoComplete="current-password"
              required
            />
          </label>
          <label>
            <span>{t("totp")}</span>
            <input
              name="totpCode"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              required
            />
          </label>
          <button className="primary-button wide" type="submit">
            <KeyRound size={18} />
            {t("signIn")}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="admin-shell">
      <header className="topbar">
        <BrandMark />
        <div className="topbar-actions">
          <LocaleToggle />
          <button className="icon-text-button" onClick={logout} type="button">
            <LogOut size={18} />
            {t("signOut")}
          </button>
        </div>
      </header>

      <section className="admin-hero">
        <div>
          <span className="eyebrow">{session.username}</span>
          <h1>{t("admin")}</h1>
          <p>{t("adminSubtitle")}</p>
        </div>
        <div className="metric-row">
          <Metric label={t("files")} value={files.length.toString()} />
          <Metric label={t("fileSize")} value={formatBytes(totalBytes)} />
        </div>
      </section>

      <ThemeSwitcher
        selectedTheme={publicTheme}
        savedTheme={savedPublicTheme}
        savingTheme={themeSaving}
        onSelect={savePublicTheme}
      />

      {toast && (
        <button className="toast" type="button" onClick={() => setToast(null)}>
          {toast}
        </button>
      )}

      {shareReveal && (
        <section className="share-reveal" aria-live="polite">
          <button
            className="icon-button close"
            type="button"
            onClick={() => setShareReveal(null)}
            aria-label={t("cancel")}
          >
            <X size={17} />
          </button>
          <span className="eyebrow">
            <CheckCircle2 size={15} />
            {t("shareCreated")}
          </span>
          <div className="share-code">{shareReveal.passkey}</div>
          <div className="share-link">{shareRevealUrl}</div>
          <div className="share-reveal-actions">
            <button
              className="secondary-button"
              type="button"
              onClick={async () =>
                setToast(
                  (await copyText(shareReveal.passkey))
                    ? t("passkeyCopied")
                    : t("copyFailed"),
                )
              }
            >
              <KeyRound size={17} />
              {t("passkey")}
            </button>
            <button
              className="secondary-button"
              type="button"
              onClick={async () =>
                copyShareDetails(shareRevealUrl, shareReveal.passkey)
              }
            >
              <Link2 size={17} />
              {t("shareLink")}
            </button>
          </div>
        </section>
      )}

      <section className={`upload-zone ${dragActive ? "is-dragging" : ""}`}>
        <label
          onDragEnter={(event) => {
            event.preventDefault();
            setDragActive(true);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={(event) => {
            if (
              !event.currentTarget.contains(event.relatedTarget as Node | null)
            )
              setDragActive(false);
          }}
          onDrop={(event) => {
            event.preventDefault();
            setDragActive(false);
            const file = event.dataTransfer.files[0];
            if (file) startUpload(file);
          }}
        >
          <UploadCloud size={30} />
          <strong>{t("upload")}</strong>
          <span>{t("dropFile")}</span>
          <small>{t("uploadHint")}</small>
          <input
            type="file"
            onChange={(event) =>
              event.target.files?.[0] && startUpload(event.target.files[0])
            }
          />
        </label>
        {uploadState.status !== "idle" && (
          <div className="upload-progress">
            <span>{uploadState.name}</span>
            <div className="progress-bar">
              <i style={{ width: `${uploadState.progress}%` }} />
            </div>
            <strong>{uploadState.progress}%</strong>
            <div className="upload-meta">
              <span>
                {t("uploaded")}: {formatBytes(uploadState.bytesUploaded)} /{" "}
                {formatBytes(uploadState.bytesTotal)}
              </span>
              <span>
                {t("speed")}: {uploadMetrics.speed}
              </span>
              <span>
                {t("eta")}: {uploadMetrics.eta}
              </span>
            </div>
            {uploadState.status === "uploading" && (
              <button
                className="icon-button"
                type="button"
                aria-label={t("pause")}
                onClick={() => {
                  uploadRef.current?.abort();
                  setUploadState((current) => ({
                    ...current,
                    status: "paused",
                  }));
                }}
              >
                <Pause size={18} />
              </button>
            )}
            {uploadState.status === "paused" && (
              <button
                className="icon-button"
                type="button"
                aria-label={t("resume")}
                onClick={() => {
                  uploadRef.current?.start();
                  setUploadState((current) => ({
                    ...current,
                    status: "uploading",
                  }));
                }}
              >
                <Play size={18} />
              </button>
            )}
            {uploadState.status === "error" && (
              <div className="status-line error">{uploadState.error}</div>
            )}
          </div>
        )}
      </section>

      <section className="file-section">
        <div className="section-heading">
          <h2>{t("files")}</h2>
          <FilePlus2 size={22} />
        </div>
        {files.length === 0 && (
          <div className="empty-state">{t("noFiles")}</div>
        )}
        <div className="file-grid">
          {files.map((file) => (
            <article className="file-card" key={file.id}>
              <div className="file-card-head">
                <div>
                  <h3>{file.originalName}</h3>
                  <p>
                    {formatBytes(file.sizeBytes)} · {file.mimeType}
                  </p>
                </div>
                <div className="delete-cluster">
                  {confirmDeleteId === file.id ? (
                    <>
                      <button
                        className="secondary-button danger"
                        type="button"
                        onClick={() => deleteFile(file)}
                      >
                        <AlertTriangle size={17} />
                        {t("confirmDelete")}
                      </button>
                      <button
                        className="icon-button"
                        type="button"
                        onClick={() => setConfirmDeleteId(null)}
                        aria-label={t("cancel")}
                      >
                        <X size={18} />
                      </button>
                    </>
                  ) : (
                    <button
                      className="icon-button danger"
                      type="button"
                      onClick={() => setConfirmDeleteId(file.id)}
                      aria-label={t("delete")}
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              </div>
              <div className="share-create-row">
                <label>
                  <span>{t("expiry")}</span>
                  <input
                    type="datetime-local"
                    value={expiryByFile[file.id] ?? ""}
                    onChange={(event) =>
                      setExpiryByFile((current) => ({
                        ...current,
                        [file.id]: event.target.value,
                      }))
                    }
                  />
                </label>
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => createShare(file)}
                >
                  <KeyRound size={17} />
                  {t("createShare")}
                </button>
              </div>
              <div className="share-list">
                {file.shares.map((share) => {
                  const publicUrl = shareUrl(share.token, locale);
                  const expired = Boolean(
                    share.expiresAt &&
                    new Date(share.expiresAt).getTime() <= Date.now(),
                  );
                  const status = expired
                    ? t("expired")
                    : share.enabled
                      ? t("enabled")
                      : t("disabled");
                  return (
                    <div className="share-row" key={share.id}>
                      <div>
                        <strong
                          className={`status-chip ${expired ? "expired" : share.enabled ? "enabled" : "disabled"}`}
                        >
                          {status}
                        </strong>
                        <small>
                          {t("downloads")}: {share.downloadCount} ·{" "}
                          {t("expires")}:{" "}
                          {share.expiresAt
                            ? formatDate(share.expiresAt, locale)
                            : t("never")}
                          {share.lastDownloadedAt
                            ? ` · ${t("lastDownload")}: ${formatDate(share.lastDownloadedAt, locale)}`
                            : ""}
                        </small>
                        <code>{publicUrl}</code>
                        <code className="share-passkey">
                          {t("passkey")}:{" "}
                          {share.passkey ?? t("passkeyUnavailable")}
                        </code>
                      </div>
                      <div className="share-actions">
                        <button
                          className="icon-button"
                          type="button"
                          title={t("copy")}
                          onClick={async () =>
                            copyShareDetails(publicUrl, share.passkey)
                          }
                        >
                          <Copy size={17} />
                        </button>
                        <button
                          className="icon-button"
                          type="button"
                          title={t("regenerate")}
                          onClick={() => regenerate(share)}
                        >
                          <RefreshCcw size={17} />
                        </button>
                        <button
                          className="icon-button"
                          type="button"
                          title={share.enabled ? t("disable") : t("enable")}
                          onClick={() => updateShare(share, !share.enabled)}
                        >
                          <CalendarClock size={17} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}

function shareDetailsText(publicUrl: string, passkey: string): string {
  return `links: ${publicUrl}, password: ${passkey}`;
}

function uploadDetails(uploadState: UploadState): {
  speed: string;
  eta: string;
} {
  if (
    !uploadState.startedAt ||
    uploadState.bytesUploaded <= 0 ||
    uploadState.status === "paused"
  ) {
    return { speed: "-", eta: "-" };
  }

  const elapsedSeconds = Math.max(
    1,
    (Date.now() - uploadState.startedAt) / 1000,
  );
  const bytesPerSecond = uploadState.bytesUploaded / elapsedSeconds;
  const remainingBytes = Math.max(
    0,
    uploadState.bytesTotal - uploadState.bytesUploaded,
  );

  return {
    speed: `${formatBytes(bytesPerSecond)}/s`,
    eta:
      remainingBytes > 0
        ? formatDuration(remainingBytes / bytesPerSecond)
        : "0s",
  };
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function ThemeSwitcher({
  selectedTheme,
  savedTheme,
  savingTheme,
  onSelect,
}: {
  selectedTheme: ShareTheme;
  savedTheme: ShareTheme;
  savingTheme: ShareTheme | null;
  onSelect: (theme: ShareTheme) => void;
}) {
  const { t } = useI18n();

  return (
    <section className="theme-console" aria-label={t("publicTheme")}>
      <div className="theme-console-head">
        <span className="eyebrow">
          <Palette size={15} />
          {t("publicTheme")}
        </span>
        <span className={`theme-save-state ${savingTheme ? "is-saving" : ""}`}>
          {savingTheme ? t("savingTheme") : t("themeSavedState")}
        </span>
      </div>
      <div className="theme-options">
        {SHARE_THEMES.map((theme) => {
          const selected = selectedTheme === theme;
          const saved = savedTheme === theme;
          const saving = savingTheme === theme;
          return (
            <button
              className={`theme-option theme-${theme} ${selected ? "is-selected" : ""}`}
              type="button"
              aria-pressed={selected}
              disabled={Boolean(savingTheme)}
              key={theme}
              onClick={() => onSelect(theme)}
            >
              <span className="theme-preview" aria-hidden="true">
                <i />
                <i />
                <i />
                {theme === "cinematic-vault" ? (
                  <ShieldCheck size={18} />
                ) : (
                  <RadioTower size={18} />
                )}
              </span>
              <span className="theme-option-copy">
                <strong>
                  {theme === "cinematic-vault"
                    ? t("themeCinematic")
                    : t("themeCyber")}
                </strong>
                <small>
                  {saving
                    ? t("savingTheme")
                    : saved
                      ? t("themeActive")
                      : selected
                        ? t("themeSelected")
                        : ""}
                </small>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
